import { computed, inject } from '@angular/core';
import { signalStore, withState, withComputed, withMethods, patchState } from '@ngrx/signals';
import { IndexCardsService } from './indexcards.service';
import { IndexCards, Titler, Grupper, Afdelinger } from './IndexCards';
import { AlertService } from 'app/core/alerts/alert.service';
import { forkJoin, Subject, EMPTY } from 'rxjs';
import { concatMap, catchError, tap } from 'rxjs/operators';
import { TimesatsNavne } from '@store/models';
import { TimesatsNavneService } from '@store';
import { rateColumns } from './timesats-columns';

export interface IndexCardsState {
    timesatsNavne: TimesatsNavne[];
    loading: boolean;
    loadError: boolean;
    users: IndexCards[];
    titler: Titler[];
    grupper: Grupper[];
    afdelinger: Afdelinger[];
}

const initialState: IndexCardsState = {
    timesatsNavne: [],
    loading: false,
    loadError: false,
    users: [],
    titler: [],
    grupper: [],
    afdelinger: [],
};

function hasDateChanged(oldVal: Date | string | null | undefined, newVal: Date | string | null | undefined): boolean {
    const newDate = newVal ? new Date(newVal) : null;
    const oldDate = oldVal ? new Date(oldVal) : null;

    if (!newDate && !oldDate) { return false; }
    if (!newDate || !oldDate) { return true; }

    return !(
        newDate.getFullYear() === oldDate.getFullYear() &&
        newDate.getMonth() === oldDate.getMonth() &&
        newDate.getDate() === oldDate.getDate()
    );
}

export const IndexCardsStore = signalStore(
    { providedIn: 'root' },

    withState(initialState),

    withComputed(store => ({ rateColumns: computed(() => rateColumns(store.timesatsNavne())) })),
    withMethods((store, service = inject(IndexCardsService), alert = inject(AlertService), rates = inject(TimesatsNavneService)) => {
        // Serialize updates so an older response cannot overwrite a newer edit.
        const updates = new Subject<{ cardId: number; key: keyof IndexCards; value: IndexCards[keyof IndexCards] }>();
        updates.pipe(concatMap(({ cardId, key, value }) => {
            const previous = store.users().find(card => card.cardId === cardId)?.[key];
            // Compare dates only after preceding queued edits have settled.
            if (key === 'ansaettelsesdato' || key === 'foedselsdato' || key === 'fratraadt') {
                if (!hasDateChanged(previous as Date | string, value as Date | string)) { return EMPTY; }
                value = value ? new Date(value as string) : null;
            }
            if (Object.is(previous, value)) { return EMPTY; }
            patchState(store, {
                users: store.users().map(card =>
                    card.cardId === cardId ? { ...card, [key]: value } : card
                )
            });
            return service.updateIndexCard(cardId, key, value).pipe(
                tap(updated => patchState(store, {
                    users: store.users().map(card =>
                        card.cardId === cardId ? { ...card, [key]: updated[key] } : card
                    )
                })),
                catchError(() => {
                    patchState(store, {
                        users: store.users().map(card =>
                            card.cardId === cardId ? { ...card, [key]: previous } : card
                        )
                    });
                    alert.createAlert('Ændringen kunne ikke gemmes. Prøv igen.', 'danger');
                    return EMPTY;
                })
            );
        })).subscribe();
        return {

            init() {
                patchState(store, { loading: true, loadError: false });
                forkJoin({
                    timesatsNavne: rates.getTimesatsNavne(),
                    users: service.getAllByOffice(),
                    titler: service.getAllTitler(),
                    grupper: service.getAllGrupper(),
                    afdelinger: service.getAllAfdelinger(),
                }).subscribe({ next: ({ users, titler, grupper, afdelinger, timesatsNavne }) => {
                    patchState(store, {
                        timesatsNavne, loading: false,
                        users,
                        titler,
                        grupper,
                        afdelinger
                    });
                }, error: () => patchState(store, { loading: false, loadError: true }) });
            },

            updateIndexCard<K extends keyof IndexCards>(cardId: number, key: K, value: IndexCards[K]) {
                updates.next({ cardId, key, value });
            },

            async deleteGruppe(gruppeId: number): Promise<boolean> {
                try {
                    await service.deleteGruppe(gruppeId).toPromise();
                    patchState(store, {
                        grupper: store.grupper().filter(group => group.id !== gruppeId),
                        users: store.users().map(user => {
                            const ids: string[] = user.grupper?.match(/\d+/g) ?? [];
                            if (!ids.includes(String(gruppeId))) { return user; }
                            return { ...user, grupper: ids.filter(id => id !== String(gruppeId)).sort((a, b) => Number(a) - Number(b)).join('-') };
                        })
                    });
                    return true;
                } catch {
                    return false;
                }
            },

            async deleteAfdeling(afdelingId: number): Promise<boolean> {
                try {
                    await service.deleteAfdeling(afdelingId).toPromise();
                    patchState(store, {
                        afdelinger: store.afdelinger().filter(item => item.id !== afdelingId),
                        users: store.users().map(user => user.afdeling === afdelingId ? { ...user, afdeling: undefined } : user)
                    });
                    return true;
                } catch {
                    return false;
                }
            },

            async createAfdeling(name: string): Promise<boolean> {
                try {
                    const created = await service.createAfdeling(name).toPromise();
                    patchState(store, { afdelinger: [...store.afdelinger(), created] });
                    return true;
                } catch {
                    return false;
                }
            },

            async createGruppe(name: string): Promise<boolean> {
                try {
                    const created = await service.createGruppe(name).toPromise();
                    patchState(store, { grupper: [...store.grupper(), created] });
                    return true;
                } catch {
                    return false;
                }
            },
        };
    })
);
